import React, { useRef } from 'react';
import * as Sentry from '@sentry/react';
import { useDeleteChannelMessageMutation } from 'shared/types/graphql';
import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Button,
  Text,
  useToast,
} from '@televet/kibble-ui';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';

interface IDeleteMessageAlertDialogProps {
  messageId: string | null;
  onClose: () => void;
}

const DeleteMessageAlertDialog = ({ messageId, onClose }: IDeleteMessageAlertDialogProps): JSX.Element => {
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const { trackEvent } = useTracking();
  const toast = useToast();

  const [deleteMessage] = useDeleteChannelMessageMutation({
    refetchQueries: ['getChannelViewChannelMessages'],
  });

  const handleDeleteMessageClick = async (): Promise<void> => {
    trackEvent(ConversationEvents.ChannelDeleteMessage);
    try {
      await deleteMessage({
        variables: {
          where: {
            id: messageId,
          },
        },
      });
    } catch (e) {
      Sentry.captureException(e);
      toast({
        status: 'error',
        title: 'Whoops! An error occurred while deleting your message. Please try again.',
      });
      console.error(e);
    } finally {
      onClose();
    }
  };

  return (
    <AlertDialog isOpen={!!messageId} isCentered={true} leastDestructiveRef={cancelRef} onClose={onClose}>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="xl" fontWeight="bold">
            Are you sure you want to delete this message?
          </AlertDialogHeader>
          <AlertDialogBody>
            <Text>You&apos;ll no longer be able to see this message or its attachments.</Text>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button variant="tertiary" ref={cancelRef} onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primaryDestructive" onClick={handleDeleteMessageClick} ml={3}>
              Delete Message
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default DeleteMessageAlertDialog;
